/**
 * The blind-entry reveal on one entry row (PHASE_blind-entry-reveal.md, Copeland
 * 2026-10-30, amending D-1 at the five physical-count areas).
 *
 * **The first entry is blind; BC on hand appears only after that line has saved.**
 * The guard is the server's, not this file's: `…/reveal` answers 409 for an item
 * with no saved line, so nothing here "hides" a figure the client already holds.
 * This component asks only once the line is on the server (`saved`), and while a
 * save is merely queued on the iPad it says so instead of asking.
 *
 * - **Never prefilled, never persisted.** What this renders comes from a live read
 *   and goes nowhere: not into the box, not into IndexedDB drafts.
 * - **A recount prompt never blocks.** Past either ruled swing test the row says
 *   exactly the ruled sentence and offers Confirm (keep the saved number) and
 *   Correct (back to the box; the edit goes through the ordinary line PUT, and the
 *   query key carries the saved quantity, so the fresh figure is revealed again).
 * - **Unreadable BC is an error, never a zero.**
 *
 * Every number is a string the server sent. The only thing done to one here is
 * dropping a leading minus to read "off BC" aloud — sign is carried by the label.
 */
import { useState } from "react";

import { ApiError } from "../api/client";
import { useLineReveal } from "../api/queries";
import type { CountLine, CountListItem, LineReveal } from "../api/types";

export interface RevealCellProps {
  areaId: string;
  sessionId: string;
  item: CountListItem;
  /** The line as staging holds it — null until a save has reached the server. */
  saved: CountLine | undefined;
  /** A save is queued on this device and has not reached the server. */
  queued: boolean;
  onCorrect: () => void;
}

const absolute = (value: string) => value.replace(/^-/, "");

export function offLabel(reveal: LineReveal): { diff: string; unit: string; baseNote: string | null } | null {
  const entryUnit = reveal.unit_of_measure_code ?? reveal.base_unit_of_measure ?? "";
  const base = reveal.base_unit_of_measure ?? "";
  if (reveal.diff_entry_qty !== null) {
    const differs = entryUnit !== "" && base !== "" && entryUnit.toUpperCase() !== base.toUpperCase();
    return {
      diff: absolute(reveal.diff_entry_qty),
      unit: entryUnit,
      baseNote:
        differs && reveal.diff_base_qty !== null ? `(${absolute(reveal.diff_base_qty)} ${base})` : null,
    };
  }
  if (reveal.diff_base_qty !== null) {
    return { diff: absolute(reveal.diff_base_qty), unit: base, baseNote: null };
  }
  return null; // BC held no factor for the counted unit: no difference is shown.
}

function onHandLabel(reveal: LineReveal): string {
  const base = reveal.base_unit_of_measure ?? "";
  const entryUnit = reveal.unit_of_measure_code ?? "";
  const differs = entryUnit !== "" && base !== "" && entryUnit.toUpperCase() !== base.toUpperCase();
  if (differs && reveal.on_hand_entry_qty !== null) {
    return `BC on hand: ${reveal.on_hand_entry_qty} ${entryUnit} (${reveal.on_hand_base_qty} ${base})`;
  }
  return `BC on hand: ${reveal.on_hand_base_qty} ${base}`.trim();
}

function signed(reveal: LineReveal): string | null {
  const entry = reveal.diff_entry_qty ?? reveal.diff_base_qty;
  if (entry === null) return null;
  const unit =
    reveal.diff_entry_qty !== null
      ? (reveal.unit_of_measure_code ?? reveal.base_unit_of_measure ?? "")
      : (reveal.base_unit_of_measure ?? "");
  const plus = !entry.startsWith("-") && entry !== "0" ? "+" : "";
  return `${plus}${entry} ${unit} vs BC`.trim();
}

export function RevealCell({ areaId, sessionId, item, saved, queued, onCorrect }: RevealCellProps) {
  const itemNo = item.item_no;
  const savedQty = saved?.counted_qty ?? "";
  const savedUnit = saved?.unit_of_measure_code ?? null;
  const query = useLineReveal(areaId, sessionId, itemNo, savedQty, savedUnit, saved !== undefined && !queued);
  // Local on purpose: confirming writes nothing, and a corrected quantity is a
  // different key, so the prompt returns for it if it is still off.
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null);
  const key = `${savedQty}|${savedUnit ?? ""}`;

  if (queued) {
    return (
      <div className="item-row__bc" data-testid={`reveal-offline-${itemNo}`}>
        <span className="muted small">Saved offline — BC check after sync</span>
      </div>
    );
  }
  if (saved === undefined) return null;
  if (query.isLoading) {
    return (
      <div className="item-row__bc" data-testid={`reveal-loading-${itemNo}`}>
        <span className="muted small">Checking BC…</span>
      </div>
    );
  }
  // 409 = the server holds no saved quantity for this item (a clear that landed
  // between render and read). There is nothing to reveal and nothing to apologise for.
  if (query.error instanceof ApiError && query.error.status === 409) return null;
  if (query.error || !query.data) {
    return (
      <div className="item-row__bc" data-testid={`reveal-error-${itemNo}`}>
        <p className="error-text small" role="alert">
          Could not read BC on hand: {(query.error as Error | null)?.message ?? "no answer"}. No
          quantity is shown — counting is not blocked.{" "}
          <button type="button" className="ghost" onClick={() => void query.refetch()}>
            Try again
          </button>
        </p>
      </div>
    );
  }

  const reveal = query.data;
  const diff = signed(reveal);
  const off = offLabel(reveal);
  const flagged = reveal.flags.length > 0;
  const prompting = flagged && off !== null && confirmedKey !== key;

  return (
    <div className="item-row__bc" data-testid={`reveal-${itemNo}`}>
      <span className="muted small" data-testid={`reveal-onhand-${itemNo}`}>
        {onHandLabel(reveal)}
      </span>
      {diff && (
        <span className="chip" data-testid={`reveal-diff-${itemNo}`}>
          {diff}
        </span>
      )}
      {prompting && off && (
        <div className="reveal-prompt" role="status" data-testid={`reveal-prompt-${itemNo}`}>
          <p>
            This is {off.diff} {off.unit} off BC. Please recount, then confirm or correct.
          </p>
          {off.baseNote && <p className="muted small">{off.baseNote}</p>}
          <div className="reveal-prompt__actions">
            <button
              type="button"
              onClick={() => setConfirmedKey(key)}
              data-testid={`reveal-confirm-${itemNo}`}
            >
              Confirm count
            </button>
            <button type="button" className="ghost" onClick={onCorrect} data-testid={`reveal-correct-${itemNo}`}>
              Correct
            </button>
          </div>
        </div>
      )}
      {flagged && !prompting && (
        <span className="muted small" data-testid={`reveal-confirmed-${itemNo}`}>
          count confirmed
        </span>
      )}
    </div>
  );
}
